import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { leadsApi, usersApi, knowledgeApi } from '../services/api';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger,
} from '../components/ui/dialog';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../components/ui/select';
import {
  ArrowLeft, Mail, Phone, Coffee, FileText, MessageSquare,
  Calendar, CheckCircle, Circle, Plus, Send, BookOpen,
  Edit2, Trash2, Clock, User, Building2, Sparkles
} from 'lucide-react';
import { toast } from 'sonner';

const EAGLE_MASCOT = 'https://customer-assets.emergentagent.com/job_fa5ebc01-0c7f-4b55-8e38-1fa2a64145ae/artifacts/h25ygx96_Transparent%20BG%20Eagle.png';

const activityTypes = [
  { value: 'email', label: 'Send Email', icon: Mail, color: '#106EFD' },
  { value: 'call', label: 'Phone Call', icon: Phone, color: '#37C563' },
  { value: 'coffee', label: 'Coffee Meeting', icon: Coffee, color: '#FF801D' },
  { value: 'article', label: 'Share Article', icon: BookOpen, color: '#E171EA' },
  { value: 'text', label: 'Text Message', icon: MessageSquare, color: '#40CFDE' },
  { value: 'meeting', label: 'Meeting', icon: Calendar, color: '#FFD166' },
  { value: 'note', label: 'Add Note', icon: FileText, color: '#6B7280' },
];

const stages = [
  { value: 'prospect', label: 'Prospect', color: '#106EFD' },
  { value: 'pitching', label: 'Pitching', color: '#40CFDE' },
  { value: 'secured_lead', label: 'Secured Lead', color: '#FFD166' },
  { value: 'proposal_sent', label: 'Proposal Sent', color: '#E171EA' },
  { value: 'closed', label: 'Closed', color: '#37C563' },
  { value: 'abandoned', label: 'Abandoned', color: '#6B7280' },
];

const LeadDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [lead, setLead] = useState(null);
  const [activities, setActivities] = useState([]);
  const [users, setUsers] = useState([]);
  const [articles, setArticles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activityDialogOpen, setActivityDialogOpen] = useState(false);
  const [editMode, setEditMode] = useState(false);
  const [editLead, setEditLead] = useState({});
  const [newActivity, setNewActivity] = useState({
    activity_type: 'note',
    title: '',
    description: '',
    scheduled_date: '',
    notes: '',
    article_id: '',
  });

  const fetchData = useCallback(async () => {
    try {
      const [leadRes, usersRes, articlesRes] = await Promise.all([
        leadsApi.getById(id),
        usersApi.getAll(),
        knowledgeApi.getAll()
      ]);
      setLead(leadRes.data);
      setActivities(leadRes.data.activities || []);
      setUsers(usersRes.data);
      setArticles(articlesRes.data);
      setEditLead(leadRes.data);
    } catch (error) {
      toast.error('Failed to load lead');
      navigate('/leads');
    } finally {
      setLoading(false);
    }
  }, [id, navigate]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const handleStageChange = async (newStage) => {
    try {
      const updateData = { ...lead, stage: newStage };
      delete updateData.activities;
      delete updateData.id;
      delete updateData.created_by;
      delete updateData.created_at;
      delete updateData.updated_at;
      delete updateData.is_deprioritized;
      await leadsApi.update(id, updateData);
      setLead({ ...lead, stage: newStage });
      toast.success('Stage updated');
    } catch { toast.error('Failed to update stage'); }
  };

  const handleSaveEdit = async () => {
    try {
      const updateData = { ...editLead };
      delete updateData.activities;
      delete updateData.id;
      delete updateData.created_by;
      delete updateData.created_at;
      delete updateData.updated_at;
      delete updateData.is_deprioritized;
      const res = await leadsApi.update(id, updateData);
      setLead({ ...lead, ...res.data });
      setEditMode(false);
      toast.success('Lead updated');
    } catch { toast.error('Failed to update'); }
  };

  const handleCreateActivity = async (e) => {
    e.preventDefault();
    try {
      await leadsApi.createActivity(id, { ...newActivity, lead_id: id });
      setActivityDialogOpen(false);
      setNewActivity({ activity_type: 'note', title: '', description: '', scheduled_date: '', notes: '', article_id: '' });
      fetchData();
      toast.success('Activity added');
    } catch { toast.error('Failed to add activity'); }
  };

  const handleToggleActivity = async (activity) => {
    try {
      await leadsApi.updateActivity(id, activity.id, { completed: !activity.completed });
      setActivities(activities.map(a =>
        a.id === activity.id ? { ...a, completed: !a.completed } : a
      ));
    } catch { toast.error('Failed to update'); }
  };

  const handleDeleteActivity = async (activityId) => {
    try {
      await leadsApi.deleteActivity(id, activityId);
      setActivities(activities.filter(a => a.id !== activityId));
      toast.success('Activity removed');
    } catch { toast.error('Failed to delete'); }
  };

  const getActivityIcon = (type) => {
    const at = activityTypes.find(a => a.value === type);
    return at || activityTypes[activityTypes.length - 1];
  };

  const getStageInfo = (stage) => stages.find(s => s.value === stage) || stages[0];

  if (loading) return <div className="flex items-center justify-center min-h-[400px]"><div className="w-10 h-10 loading-spinner" /></div>;
  if (!lead) return null;

  const stageInfo = getStageInfo(lead.stage);

  return (
    <div className="space-y-6" data-testid="lead-detail-page">
      {/* Header */}
      <div className="flex items-center gap-4">
        <Button variant="ghost" size="icon" onClick={() => navigate('/leads')} data-testid="back-to-leads-btn">
          <ArrowLeft className="w-5 h-5" />
        </Button>
        <div className="flex-1">
          <div className="flex items-center gap-3">
            <h1 className="text-3xl font-bold text-[#1A2B5E] font-['Amaranth']" data-testid="lead-company-name">
              {lead.company_name}
            </h1>
            {lead.company_type && (
              <Badge className="bg-[#1A2B5E]/10 text-[#1A2B5E]">{lead.company_type}</Badge>
            )}
          </div>
          <p className="text-navy/60 mt-1">{lead.contact_name}</p>
        </div>
        <div className="flex items-center gap-3">
          <Select value={lead.stage} onValueChange={handleStageChange}>
            <SelectTrigger className="w-[170px]" data-testid="lead-stage-select">
              <div className="flex items-center gap-2">
                <div className="w-2.5 h-2.5 rounded-full" style={{ backgroundColor: stageInfo.color }} />
                <SelectValue />
              </div>
            </SelectTrigger>
            <SelectContent>
              {stages.map(s => (
                <SelectItem key={s.value} value={s.value}>
                  <div className="flex items-center gap-2">
                    <div className="w-2 h-2 rounded-full" style={{ backgroundColor: s.color }} />
                    {s.label}
                  </div>
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          <Button variant="outline" onClick={() => { setEditMode(!editMode); setEditLead(lead); }} data-testid="edit-lead-btn">
            <Edit2 className="w-4 h-4 mr-2" /> Edit
          </Button>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Left column: Lead info + Offer */}
        <div className="lg:col-span-1 space-y-6">
          <Card className="border-navy/10">
            <CardHeader className="pb-3">
              <CardTitle className="text-lg font-['Amaranth'] flex items-center gap-2">
                <Building2 className="w-5 h-5 text-[#40CFDE]" /> Lead Info
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              {editMode ? (
                <div className="space-y-3">
                  <div><Label>Company</Label><Input value={editLead.company_name || ''} onChange={e => setEditLead({...editLead, company_name: e.target.value})} /></div>
                  <div><Label>Type</Label>
                    <Select value={editLead.company_type || ''} onValueChange={v => setEditLead({...editLead, company_type: v})}>
                      <SelectTrigger><SelectValue placeholder="Select type" /></SelectTrigger>
                      <SelectContent>
                        {['Fintech','Attorney','Bank','Non-Bank Lender','Consultant','Credit Union','Other'].map(t => <SelectItem key={t} value={t}>{t}</SelectItem>)}
                      </SelectContent>
                    </Select>
                  </div>
                  <div><Label>Contact Name</Label><Input value={editLead.contact_name || ''} onChange={e => setEditLead({...editLead, contact_name: e.target.value})} /></div>
                  <div><Label>Email</Label><Input value={editLead.contact_email || ''} onChange={e => setEditLead({...editLead, contact_email: e.target.value})} /></div>
                  <div><Label>Phone</Label><Input value={editLead.contact_phone || ''} onChange={e => setEditLead({...editLead, contact_phone: e.target.value})} /></div>
                  <div><Label>Priority</Label>
                    <Select value={editLead.priority || 'medium'} onValueChange={v => setEditLead({...editLead, priority: v})}>
                      <SelectTrigger><SelectValue /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="high">High</SelectItem>
                        <SelectItem value="medium">Medium</SelectItem>
                        <SelectItem value="low">Low</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="flex gap-2 pt-2">
                    <Button onClick={handleSaveEdit} className="bg-[#40CFDE] text-[#1A2B5E]" data-testid="save-lead-btn">Save</Button>
                    <Button variant="outline" onClick={() => setEditMode(false)}>Cancel</Button>
                  </div>
                </div>
              ) : (
                <>
                  {lead.contact_name && <div className="flex items-center gap-2 text-sm"><User className="w-4 h-4 text-navy/40" /><span>{lead.contact_name}</span></div>}
                  {lead.contact_email && <div className="flex items-center gap-2 text-sm"><Mail className="w-4 h-4 text-navy/40" /><a href={`mailto:${lead.contact_email}`} className="text-[#106EFD] hover:underline">{lead.contact_email}</a></div>}
                  {lead.contact_phone && <div className="flex items-center gap-2 text-sm"><Phone className="w-4 h-4 text-navy/40" /><span>{lead.contact_phone}</span></div>}
                  <div className="flex items-center gap-2 text-sm"><Building2 className="w-4 h-4 text-navy/40" /><span>{lead.company_type || 'Not specified'}</span></div>
                  <div className="flex items-center gap-2 text-sm"><Clock className="w-4 h-4 text-navy/40" /><span>Last contact: {lead.last_contact ? new Date(lead.last_contact).toLocaleDateString() : 'Never'}</span></div>
                  <div className="pt-2">
                    <Badge className={lead.priority === 'high' ? 'bg-red-100 text-red-700' : lead.priority === 'medium' ? 'bg-yellow-100 text-yellow-700' : 'bg-gray-100 text-gray-600'}>
                      {(lead.priority || 'medium').charAt(0).toUpperCase() + (lead.priority || 'medium').slice(1)} Priority
                    </Badge>
                  </div>
                </>
              )}
            </CardContent>
          </Card>

          {/* Offer Card */}
          <Card className="border-navy/10">
            <CardHeader className="pb-3">
              <CardTitle className="text-lg font-['Amaranth'] flex items-center gap-2">
                <Sparkles className="w-5 h-5 text-[#E171EA]" /> Offer / Proposal
              </CardTitle>
            </CardHeader>
            <CardContent>
              {editMode ? (
                <Textarea
                  value={editLead.offer || ''}
                  onChange={e => setEditLead({...editLead, offer: e.target.value})}
                  placeholder="What would the offer be for this lead?"
                  rows={4}
                />
              ) : (
                <p className="text-sm text-navy/70 whitespace-pre-wrap">
                  {lead.offer || 'No offer defined yet. Click Edit to add one.'}
                </p>
              )}
            </CardContent>
          </Card>

          {/* Notes Card */}
          <Card className="border-navy/10">
            <CardHeader className="pb-3">
              <CardTitle className="text-lg font-['Amaranth'] flex items-center gap-2">
                <FileText className="w-5 h-5 text-[#FFD166]" /> Notes
              </CardTitle>
            </CardHeader>
            <CardContent>
              {editMode ? (
                <Textarea
                  value={editLead.notes || ''}
                  onChange={e => setEditLead({...editLead, notes: e.target.value})}
                  placeholder="Internal notes for the team..."
                  rows={4}
                />
              ) : (
                <p className="text-sm text-navy/70 whitespace-pre-wrap">
                  {lead.notes || 'No notes yet.'}
                </p>
              )}
            </CardContent>
          </Card>

          {/* Quick Actions */}
          <Card className="border-navy/10 bg-[#F5F8FF]">
            <CardHeader className="pb-3">
              <CardTitle className="text-lg font-['Amaranth']">Quick Actions</CardTitle>
            </CardHeader>
            <CardContent className="flex flex-wrap gap-2">
              {lead.send_email && <Badge className="bg-[#106EFD]/10 text-[#106EFD]"><Mail className="w-3 h-3 mr-1" /> Send Email</Badge>}
              {lead.send_article && <Badge className="bg-[#E171EA]/10 text-[#E171EA]"><BookOpen className="w-3 h-3 mr-1" /> Share Article</Badge>}
              {lead.send_text && <Badge className="bg-[#40CFDE]/10 text-[#40CFDE]"><MessageSquare className="w-3 h-3 mr-1" /> Text</Badge>}
              {!lead.send_email && !lead.send_article && !lead.send_text && (
                <p className="text-sm text-navy/50">No quick actions flagged</p>
              )}
            </CardContent>
          </Card>
        </div>

        {/* Right column: Activity timeline */}
        <div className="lg:col-span-2 space-y-4">
          <div className="flex items-center justify-between">
            <h2 className="text-xl font-bold text-[#1A2B5E] font-['Amaranth']">Activity Timeline</h2>
            <Dialog open={activityDialogOpen} onOpenChange={setActivityDialogOpen}>
              <DialogTrigger asChild>
                <Button className="bg-[#40CFDE] text-[#1A2B5E] hover:bg-[#40CFDE]/90" data-testid="add-activity-btn">
                  <Plus className="w-4 h-4 mr-2" /> Log Activity
                </Button>
              </DialogTrigger>
              <DialogContent className="sm:max-w-[500px]">
                <DialogHeader>
                  <DialogTitle className="font-['Amaranth']">Log Activity</DialogTitle>
                </DialogHeader>
                <form onSubmit={handleCreateActivity} className="space-y-4 mt-4">
                  <div className="space-y-2">
                    <Label>Activity Type</Label>
                    <div className="grid grid-cols-4 gap-2">
                      {activityTypes.map(at => {
                        const Icon = at.icon;
                        return (
                          <button
                            key={at.value}
                            type="button"
                            onClick={() => setNewActivity({...newActivity, activity_type: at.value, title: newActivity.title || at.label})}
                            className={`p-3 rounded-lg border text-center transition-all ${newActivity.activity_type === at.value ? 'border-[#40CFDE] bg-[#40CFDE]/10' : 'border-navy/10 hover:border-navy/30'}`}
                            data-testid={`activity-type-${at.value}`}
                          >
                            <Icon className="w-5 h-5 mx-auto mb-1" style={{ color: at.color }} />
                            <span className="text-xs text-navy/70">{at.label}</span>
                          </button>
                        );
                      })}
                    </div>
                  </div>
                  <div><Label>Title *</Label><Input value={newActivity.title} onChange={e => setNewActivity({...newActivity, title: e.target.value})} placeholder="Activity title" required data-testid="activity-title-input" /></div>
                  <div><Label>Description</Label><Textarea value={newActivity.description} onChange={e => setNewActivity({...newActivity, description: e.target.value})} placeholder="Details about this activity..." rows={3} /></div>
                  <div><Label>Scheduled Date</Label><Input type="datetime-local" value={newActivity.scheduled_date} onChange={e => setNewActivity({...newActivity, scheduled_date: e.target.value})} /></div>
                  {newActivity.activity_type === 'article' && (
                    <div className="space-y-2">
                      <Label>Select Article from Knowledge Base</Label>
                      <Select value={newActivity.article_id} onValueChange={v => setNewActivity({...newActivity, article_id: v})}>
                        <SelectTrigger><SelectValue placeholder="Choose article..." /></SelectTrigger>
                        <SelectContent>
                          {articles.map(a => <SelectItem key={a.id} value={a.id}>{a.title}</SelectItem>)}
                        </SelectContent>
                      </Select>
                    </div>
                  )}
                  <div><Label>Notes</Label><Textarea value={newActivity.notes} onChange={e => setNewActivity({...newActivity, notes: e.target.value})} placeholder="Notes for the team..." rows={2} /></div>
                  <div className="flex justify-end gap-3 pt-2">
                    <Button type="button" variant="outline" onClick={() => setActivityDialogOpen(false)}>Cancel</Button>
                    <Button type="submit" className="bg-[#40CFDE] text-[#1A2B5E]" data-testid="save-activity-btn"><Send className="w-4 h-4 mr-2" /> Log Activity</Button>
                  </div>
                </form>
              </DialogContent>
            </Dialog>
          </div>

          {/* Timeline */}
          {activities.length === 0 ? (
            <Card className="border-navy/10">
              <CardContent className="py-12 text-center">
                <img src={EAGLE_MASCOT} alt="Eagle" className="w-16 h-16 mx-auto mb-4" />
                <h3 className="text-lg font-['Amaranth'] text-[#1A2B5E] mb-2">No activities yet</h3>
                <p className="text-sm text-navy/50">Log your first touchpoint to start tracking this lead</p>
              </CardContent>
            </Card>
          ) : (
            <div className="space-y-3">
              {activities.map(activity => {
                const at = getActivityIcon(activity.activity_type);
                const Icon = at.icon;
                return (
                  <Card key={activity.id} className={`border-navy/10 transition-all ${activity.completed ? 'opacity-60' : ''}`} data-testid={`activity-${activity.id}`}>
                    <CardContent className="p-4">
                      <div className="flex items-start gap-3">
                        <button
                          onClick={() => handleToggleActivity(activity)}
                          className="mt-0.5 flex-shrink-0"
                          data-testid={`toggle-activity-${activity.id}`}
                        >
                          {activity.completed ? (
                            <CheckCircle className="w-5 h-5 text-[#37C563]" />
                          ) : (
                            <Circle className="w-5 h-5 text-navy/30 hover:text-[#40CFDE]" />
                          )}
                        </button>
                        <div className="w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0" style={{ backgroundColor: `${at.color}15` }}>
                          <Icon className="w-4 h-4" style={{ color: at.color }} />
                        </div>
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2">
                            <h4 className={`font-medium text-[#1A2B5E] ${activity.completed ? 'line-through' : ''}`}>{activity.title}</h4>
                            <Badge className="text-xs" style={{ backgroundColor: `${at.color}15`, color: at.color }}>{at.label}</Badge>
                          </div>
                          {activity.description && <p className="text-sm text-navy/60 mt-1">{activity.description}</p>}
                          {activity.notes && <p className="text-sm text-navy/50 mt-1 italic">Notes: {activity.notes}</p>}
                          <div className="flex items-center gap-4 mt-2 text-xs text-navy/40">
                            <span>{activity.created_by_name}</span>
                            <span>{new Date(activity.created_at).toLocaleDateString()}</span>
                            {activity.scheduled_date && <span className="flex items-center gap-1"><Calendar className="w-3 h-3" /> {new Date(activity.scheduled_date).toLocaleDateString()}</span>}
                          </div>
                        </div>
                        <button onClick={() => handleDeleteActivity(activity.id)} className="text-navy/30 hover:text-red-500 transition-colors" data-testid={`delete-activity-${activity.id}`}>
                          <Trash2 className="w-4 h-4" />
                        </button>
                      </div>
                    </CardContent>
                  </Card>
                );
              })}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default LeadDetail;
